import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetCartQuery } from '@/components/orders/services/orderApi';
import { setCartCount } from '@/components/properties/property-view/slice/OrderCartSlice';
import { getPermissionForAction } from '@/util/permissions';
import type { RootState } from '@/store/store';

/**
 * Cart-count badge state shared by the desktop and mobile headers.
 *
 * Subscribes to RTK Query for the user's cart and mirrors the totals into
 * the existing `orderCart` slice that the rest of the app already reads.
 * Cart mutations elsewhere invalidate the 'Cart' tag, which refetches this
 * query automatically.
 */
export function useCartBadgeCount(isLogin: boolean): {
  cartCount: number;
  showBadge: boolean;
} {
  const dispatch = useDispatch();
  const cartCount = useSelector(
    (state: RootState) => state.orderCart.cartCount
  );

  const cartUserId = isLogin ? localStorage.getItem('userId') ?? '' : '';
  // Skip for roles the table denies 'GET :Cart' (INTERNAL_POST_PROCESSING): the
  // headers that call this mount on every authenticated page, so an unskipped
  // denied query would 403 on each navigation and drive baseQueryWithAuth's
  // single-use refresh-token rotation → eventual spurious logout (DV-2751).
  const { data: cartData } = useGetCartQuery(cartUserId, {
    skip: !cartUserId || !getPermissionForAction('Cart', 'GET')
  });

  useEffect(() => {
    if (cartData) {
      const itemsCount = cartData.items?.length ?? 0;
      const editPhotoOrdersCount = cartData.editPhotoOrders?.length ?? 0;
      dispatch(setCartCount(itemsCount + editPhotoOrdersCount));
    }
  }, [cartData, dispatch]);

  return { cartCount, showBadge: cartCount > 0 };
}
